import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { BehaviorSubject, tap } from 'rxjs';
import { livepassEndpoints } from '../config/livepass-endpoints.config';
import { extractCartRecord } from '../utils/cart-checkout-merge.util';
import { isShowSeatingEnabled } from '../utils/seating-enabled.util';
import { DataService } from './data.service';

export interface CartSyncBody {
  event_id: string | number;
  ticket_types: Array<{ id: string | number; quantity: number }>;
}

export interface HoldSeatsBody {
  event_id: string | number;
  show_id: string | number;
  seat_codes: string[];
}

export interface ReleaseSeatsBody {
  show_id: string | number;
  seat_ids: Array<number | string>;
  hold_token: string;
}

@Injectable({
  providedIn: 'root'
})
export class CartService {
  private static readonly HIDE_HEADER_CART_KEY = 'seated_hide_header_cart';

  constructor(private data: DataService) {
    this.emitHideHeaderCart();
  }

  private readonly cartHasItemsSubject = new BehaviorSubject<boolean>(false);
  readonly cartHasItems$ = this.cartHasItemsSubject.asObservable();

  /** Hide header cart icon for seated bookings — app-wide, not only booking pages. */
  private readonly hideHeaderCartSubject = new BehaviorSubject<boolean>(false);
  readonly hideHeaderCart$ = this.hideHeaderCartSubject.asObservable();
  /** Tickets page only: hide cart without locking it for the rest of the app. */
  private transientHideHeaderCart = false;

  setCartHasItems(hasItems: boolean): void {
    this.cartHasItemsSubject.next(!!hasItems);
  }

  /**
   * @param persist Keep hide across routes/refresh. False = tickets page only, until ADD TO CART.
   */
  setHideHeaderCart(hide: boolean, persist = true): void {
    if (persist) {
      this.transientHideHeaderCart = false;
      try {
        if (hide) {
          sessionStorage.setItem(CartService.HIDE_HEADER_CART_KEY, '1');
        } else {
          sessionStorage.removeItem(CartService.HIDE_HEADER_CART_KEY);
        }
      } catch {
        /* ignore */
      }
    } else {
      this.transientHideHeaderCart = !!hide;
    }
    this.emitHideHeaderCart();
  }

  /** Restore global hide from seated cart/checkout after leaving a booking page. */
  syncHideHeaderCartFromSession(): void {
    this.transientHideHeaderCart = false;
    this.emitHideHeaderCart();
  }

  private emitHideHeaderCart(): void {
    this.hideHeaderCartSubject.next(this.transientHideHeaderCart || this.shouldHideHeaderCart());
  }

  private shouldHideHeaderCart(): boolean {
    return this.readPersistedHide() || this.hasActiveSeatedCheckout();
  }

  private readPersistedHide(): boolean {
    try {
      return sessionStorage.getItem(CartService.HIDE_HEADER_CART_KEY) === '1';
    } catch {
      return false;
    }
  }

  private readCheckoutSession(): Record<string, unknown> | null {
    try {
      const raw = sessionStorage.getItem('checkout');
      if (!raw) return null;
      const parsed = JSON.parse(raw) as unknown;
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return null;
      return parsed as Record<string, unknown>;
    } catch {
      return null;
    }
  }

  private hasActiveSeatedCheckout(): boolean {
    const checkout = this.readCheckoutSession();
    if (!checkout) return false;
    const qty = Number(checkout['qty'] ?? 0);
    const items = checkout['items'];
    const hasQty =
      (Number.isFinite(qty) && qty > 0) ||
      (Array.isArray(items) &&
        items.some((row) => Number((row as { qty?: unknown })?.qty ?? 0) > 0));
    if (!hasQty) return false;
    if (checkout['seated_booking'] === true) return true;
    return isShowSeatingEnabled(
      (checkout['event'] as Record<string, unknown> | undefined) ?? null,
      (checkout['show'] as Record<string, unknown> | undefined) ?? null
    );
  }

  private isSeatedCartPayload(res: unknown): boolean {
    const cart = extractCartRecord(res);
    if (!cart) return false;
    const itemsRaw = cart['items'] ?? cart['ticket_types'] ?? cart['lines'];
    if (!Array.isArray(itemsRaw) || itemsRaw.length === 0) return false;
    if (cart['seated_booking'] === true) return true;
    const first = itemsRaw[0] as Record<string, unknown>;
    const event =
      cart['event'] && typeof cart['event'] === 'object' && !Array.isArray(cart['event'])
        ? (cart['event'] as Record<string, unknown>)
        : cart;
    const show =
      cart['show'] && typeof cart['show'] === 'object' && !Array.isArray(cart['show'])
        ? (cart['show'] as Record<string, unknown>)
        : first;
    if (first?.['seated_booking'] === true) return true;
    return isShowSeatingEnabled(event, show) || isShowSeatingEnabled(first, first);
  }

  private syncHideHeaderCartFromCartResponse(res: unknown, hasItems: boolean): void {
    if (!hasItems) {
      try {
        sessionStorage.removeItem(CartService.HIDE_HEADER_CART_KEY);
      } catch {
        /* ignore */
      }
      this.emitHideHeaderCart();
      return;
    }
    if (this.isSeatedCartPayload(res) || this.hasActiveSeatedCheckout()) {
      this.setHideHeaderCart(true);
    } else {
      this.emitHideHeaderCart();
    }
  }

  private inferHasItemsFromCartResponse(res: unknown): boolean {
    const r = res as any;
    const candidates = [
      r?.items,
      r?.data?.items,
      r?.data?.data?.items,
      r?.data?.data?.data?.items
    ];
    for (const c of candidates) {
      if (Array.isArray(c)) return c.length > 0;
    }
    return false;
  }

  private extractCartCreatedAtUtc(res: unknown): string | null {
    const r = res as any;
    const candidates = [
      r?.cart_created_at,
      r?.created_at,
      r?.data?.cart_created_at,
      r?.data?.created_at,
      r?.data?.data?.cart_created_at,
      r?.data?.data?.created_at,
      r?.data?.data?.data?.cart_created_at,
      r?.data?.data?.data?.created_at,
      r?.items?.[0]?.cart_created_at,
      r?.items?.[0]?.created_at,
      r?.data?.items?.[0]?.cart_created_at,
      r?.data?.items?.[0]?.created_at,
      r?.data?.data?.items?.[0]?.cart_created_at,
      r?.data?.data?.items?.[0]?.created_at
    ];
    for (const c of candidates) {
      if (typeof c === 'string' && c.trim()) return c.trim();
    }
    return null;
  }

  seedReviewExpiryFromCartResponse(res: unknown, fallbackToNow = false): number | null {
    const createdAtUtc = this.extractCartCreatedAtUtc(res);
    const createdMs = createdAtUtc ? Date.parse(createdAtUtc) : NaN;
    const expiryMs =
      Number.isFinite(createdMs) && createdMs > 0
        ? createdMs + 10 * 60 * 1000
        : fallbackToNow
          ? Date.now() + 10 * 60 * 1000
          : null;
    if (expiryMs == null) return null;
    try {
      sessionStorage.setItem('review_expiry_at', String(expiryMs));
    } catch {
      /* ignore */
    }
    return expiryMs;
  }

  /** POST /cart — replaces server cart for this event + ticket quantities. */
  syncCart(body: CartSyncBody): Observable<unknown> {
    return this.data.post<unknown>('cart', body).pipe(
      tap((res) => {
        const totalQty = (body.ticket_types || []).reduce((sum, t) => sum + Number(t.quantity || 0), 0);
        if (totalQty > 0) {
          this.setCartHasItems(true);
          this.seedReviewExpiryFromCartResponse(res);
        }
      })
    );
  }

  /** POST /cart/hold-seats — hold selected seat codes for this event/show. */
  holdSeats(body: HoldSeatsBody): Observable<unknown> {
    return this.data.post<unknown>(livepassEndpoints.holdSeats, body);
  }

  /** POST /seatsio/release-seats — free held seats if booking was not completed. */
  releaseSeats(body: ReleaseSeatsBody): Observable<unknown> {
    return this.data.post<unknown>(livepassEndpoints.releaseSeats, body);
  }

  /**
   * DELETE /cart — remove cart lines for an event by sending full payload.
   * Some backends require the same shape as sync (event_id + ticket_types).
   */
  removeCart(body: CartSyncBody): Observable<unknown> {
    return this.data.delete<unknown>('cart', body);
  }

  /** GET /cart — current cart (decrypted by interceptor). */
  getCart(): Observable<unknown> {
    return this.data.get<unknown>('cart').pipe(
      tap((res) => {
        const hasItems = this.inferHasItemsFromCartResponse(res);
        this.setCartHasItems(hasItems);
        this.syncHideHeaderCartFromCartResponse(res, hasItems);
        if (hasItems) {
          // If cart already exists (e.g. returning user / refresh), seed header timer from cart_created_at.
          this.seedReviewExpiryFromCartResponse(res);
        }
      })
    );
  }

  /** DELETE /cart/:id — backend supports event id and -1 (clear all). */
  removeCartLine(lineId: number | string): Observable<unknown> {
    const id = String(lineId).replace(/^\/+/, '').replace(/\/+$/, '');
    return this.data.delete<unknown>(`cart/${id}`).pipe(
      tap(() => {
        if (id === '-1') {
          this.setCartHasItems(false);
          this.setHideHeaderCart(false);
          // Clear booking-hold timer shown in header.
          sessionStorage.removeItem('review_expiry_at');
        }
      })
    );
  }

  /** DELETE /cart/{eventId} — clear cart for one event. */
  clearCartForEvent(eventId: string | number): Observable<unknown> {
    return this.removeCartLine(eventId);
  }

  /** DELETE /cart/-1 — clear entire cart. */
  clearAllCart(): Observable<unknown> {
    return this.removeCartLine(-1);
  }
}
